import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiDownload } from 'react-icons/fi';
import { invoiceAPI, pdfAPI } from '../../services/api';
import DataTable from '../../components/shared/DataTable';
import Modal from '../../components/shared/Modal';

const InvoiceListPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('');
  const [form, setForm] = useState({ issue_date: '', due_date: '', tax_rate: '0', notes: '', items: [{ description: '', quantity: 1, unit_price: '' }] });
  const navigate = useNavigate();

  const fetchInvoices = async () => {
    try {
      const params = {};
      if (filter) params.status = filter;
      const { data } = await invoiceAPI.list(params);
      setInvoices(data);
    } catch (e) {} finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchInvoices(); }, [filter]);

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: '' }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, field, value) => { const items = [...form.items]; items[idx][field] = value; setForm({ ...form, items }); };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, tax_rate: parseFloat(form.tax_rate || 0), items: form.items.map(i => ({ description: i.description, quantity: parseFloat(i.quantity || 1), unit_price: parseFloat(i.unit_price) })) };
      await invoiceAPI.create(payload);
      setShowModal(false);
      fetchInvoices();
    } catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const downloadPDF = async (id, e) => {
    e.stopPropagation();
    try {
      const { data } = await pdfAPI.downloadInvoice(id);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url; a.download = `facture-${id}.pdf`; a.click();
    } catch (err) { alert('Erreur PDF'); }
  };

  const columns = [
    { header: 'N° Facture', accessor: 'invoice_number' },
    { header: 'Client/Locataire', render: (r) => r.tenant_first_name ? `${r.tenant_first_name} ${r.tenant_last_name}` : r.client_first_name ? `${r.client_first_name} ${r.client_last_name}` : '-' },
    { header: 'Total', render: (r) => `${parseInt(r.total).toLocaleString()} XOF` },
    { header: 'Émission', render: (r) => new Date(r.issue_date).toLocaleDateString('fr-FR') },
    { header: 'Échéance', render: (r) => new Date(r.due_date).toLocaleDateString('fr-FR') },
    { header: 'Statut', render: (r) => <span className={`badge badge-${r.status === 'paid' ? 'success' : r.status === 'sent' ? 'info' : r.status === 'overdue' ? 'danger' : 'secondary'}`}>{r.status}</span> },
    { header: '', render: (r) => <button className="btn btn-sm btn-secondary" onClick={(e) => downloadPDF(r.id, e)}><FiDownload /></button>, width: '60px' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Factures</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}><FiPlus /> Nouvelle facture</button>
      </div>

      <div className="filters">
        {['', 'draft', 'sent', 'paid', 'overdue'].map(s => (
          <button key={s} className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(s)}>
            {s === '' ? 'Toutes' : s === 'draft' ? 'Brouillon' : s === 'sent' ? 'Envoyées' : s === 'paid' ? 'Payées' : 'En retard'}
          </button>
        ))}
      </div>

      {loading ? <div className="loading"><div className="spinner" /></div> : (
        <div className="card"><DataTable columns={columns} data={invoices} onRowClick={(r) => navigate(`/invoices/${r.id}`)} emptyMessage="Aucune facture" /></div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Nouvelle facture" size="lg">
        <form onSubmit={handleCreate}>
          <div className="grid-2">
            <div className="form-group"><label>Date d'émission *</label><input type="date" className="form-control" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} required /></div>
            <div className="form-group"><label>Date d'échéance *</label><input type="date" className="form-control" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} required /></div>
          </div>
          <div className="form-group"><label>TVA (%)</label><input type="number" className="form-control" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: e.target.value })} /></div>

          <h3 style={{ margin: '16px 0 8px' }}>Articles</h3>
          {form.items.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'end' }}>
              <div style={{ flex: 3 }}><input className="form-control form-control-sm" placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} required /></div>
              <div style={{ flex: 1 }}><input type="number" className="form-control form-control-sm" placeholder="Qté" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} /></div>
              <div style={{ flex: 1 }}><input type="number" className="form-control form-control-sm" placeholder="P.U." value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} required /></div>
              {form.items.length > 1 && <button type="button" className="btn btn-danger btn-sm" onClick={() => removeItem(idx)}>X</button>}
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" onClick={addItem}><FiPlus /> Ajouter un article</button>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary">Créer la facture</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default InvoiceListPage;
